import {
  Partners,
  QUOTE_TYPES,
  QUOTE_WORKFLOW_STATUSES,
  QuoteType,
  QuoteWorkflowStatus,
  Role,
  RoleType,
} from '@lockton-apps/shared/interfaces';

import { createDefinition } from '@lockton-apps/shared/util/helpers';
import { WorkflowStatusMeta } from './interface';

const getBrokerQuoteWorkflowMeta = createDefinition<WorkflowStatusMeta>({
  base: {
    label: $localize`Pending`,
    icon: 'more',
    level: 'info',
  },
  variants: {
    [QUOTE_WORKFLOW_STATUSES.NewProposal]: {
      base: {
        label: $localize`New proposal`,
        icon: 'application_new',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalSent]: {
      base: {
        label: $localize`Proposal sent`,
        icon: 'application_sent',
        level: 'info',
        supportingText: $localize`Update this status when the proposal form has been issued to the client`,
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalInProgress]: {
      base: {
        label: $localize`Proposal in progress`,
        icon: 'application_progress',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalComplete]: {
      base: {
        label: $localize`Proposal complete`,
        icon: 'application_complete',
        level: 'info',
      },
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS]: {
              base: {
                label: $localize`Proposal received`,
              },
            },
            [Partners.LSOS_ADDON]: {
              base: {
                label: $localize`Proposal received`,
                supportingText: $localize`Update this status when you have received the proposal form from the client`,
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalReleased]: {
      base: {
        label: $localize`Proposal returned`,
        icon: 'application_released',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ReviewRequired]: {
      base: {
        label: $localize`Review required`,
        icon: 'under_review',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteOnHold]: {
      base: {
        label: $localize`Quote on hold`,
        icon: 'quote_onhold',
        level: 'warning',
      },
      variants: {
        [QUOTE_TYPES.NewBusiness]: {
          base: {},
          variants: {
            [Partners.NORWAY]: {
              base: {
                label: $localize`Review required`,
                icon: 'quote_onhold',
                level: 'warning',
              },
            },
            [Partners.NORWAY_PI]: {
              base: {
                label: $localize`Review required`,
                icon: 'quote_onhold',
                level: 'warning',
              },
            },
          },
        },
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.NORWAY]: {
              base: {
                label: $localize`Review required`,
                icon: 'quote_onhold',
                level: 'warning',
              },
            },
            [Partners.NORWAY_PI]: {
              base: {
                label: $localize`Review required`,
                icon: 'quote_onhold',
                level: 'warning',
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteHE]: {
      base: {
        label: $localize`Health evaluation required`,
        icon: 'health_evaluation',
        level: 'warning',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteSent]: {
      base: {
        label: $localize`Quote sent`,
        icon: 'quote_universal_sent',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteRequested]: {
      base: {
        label: $localize`Quote requested`,
        icon: 'quote_requested',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteReady]: {
      base: {
        label: $localize`Quote ready`,
        icon: 'quote_universal',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteAccepted]: {
      base: {
        label: $localize`Quote accepted`,
        icon: 'quote_universal_accepted',
        level: 'info',
      },
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS]: {
              base: {
                label: $localize`Instructed by client`,
                icon: 'instructed',
                level: 'info',
              },
            },
            [Partners.LSOS_ADDON]: {
              base: {
                label: $localize`Instructed by client`,
                icon: 'instructed',
                level: 'info',
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.AwaitingPayment]: {
      base: {
        label: $localize`Awaiting payment`,
        icon: 'invoice_sent_universal',
        level: 'info',
        supportingText: $localize`Update this status when the invoice has been sent`,
      },
      variants: {
        [QUOTE_TYPES.NewBusiness]: {
          base: {},
          variants: {
            [Partners.NORWAY]: {
              base: {
                icon: 'payment_progress',
                level: 'warning',
              },
            },
            [Partners.NORWAY_PI]: {
              base: {
                icon: 'payment_progress',
                level: 'warning',
              },
            },
            [Partners.ACCA]: {
              base: {
                level: 'warning',
              },
            },
          },
        },
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.NORWAY]: {
              base: {
                icon: 'payment_progress',
                level: 'warning',
              },
            },
            [Partners.NORWAY_PI]: {
              base: {
                icon: 'payment_progress',
                level: 'warning',
              },
            },
            [Partners.ACCA]: {
              base: {
                level: 'warning',
              },
            },
            [Partners.LSOS]: {
              base: {
                level: 'warning',
              },
            },
            [Partners.LSOS_ADDON]: {
              base: {
                icon: 'invoice_unpaid_universal',
                supportingText: $localize`Update this status when you have sent the invoice to the client`,
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.PaymentProcessing]: {
      base: {
        label: $localize`Payment processing`,
        icon: 'payment_progress',
        level: 'warning',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.PaymentReceived]: {
      base: {
        label: $localize`Payment received`,
        icon: 'invoice_paid_universal',
        level: 'success',
      },
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS]: {
              base: {
                label: $localize`Completed`,
                icon: 'tick',
                level: 'success',
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.PolicyDocumentationSent]: {
      base: {
        label: $localize`Policy documentation sent`,
        icon: 'doc_sent',
        level: 'info',
      },
      variants: {
        [QUOTE_TYPES.NewBusiness]: {
          base: {},
          variants: {
            [Partners.NORWAY]: {
              base: {
                label: $localize`Completed`,
                icon: 'renewal_complete',
                level: 'success',
              },
            },
            [Partners.NORWAY_PI]: {
              base: {
                label: $localize`Completed`,
                icon: 'renewal_complete',
                level: 'success',
              },
            },
          },
        },
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS]: {
              base: {
                label: $localize`Completed (Addons)`,
                icon: 'tick',
                level: 'success',
              },
            },
            [Partners.LSOS_ADDON]: {
              base: {
                label: $localize`Completed (Addons)`,
                icon: 'tick',
                level: 'success',
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.Lapsed]: {
      base: {
        label: $localize`Lapsed`,
        icon: 'lapsed',
        level: 'error',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.NotTakenUp]: {
      base: {
        label: $localize`Not taken up`,
        icon: 'renewal_ntu',
        level: 'error',
      },
    },
  },
});

const getContactQuoteWorkflowMeta = createDefinition<WorkflowStatusMeta>({
  base: {
    label: $localize`Pending`,
    icon: 'more',
    level: 'info',
  },
  variants: {
    [QUOTE_WORKFLOW_STATUSES.NewProposal]: {
      base: {
        label: $localize`New proposal`,
        icon: 'application_new',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalSent]: {
      base: {
        label: $localize`Renewal open`,
        icon: 'renewal',
        level: 'info',
      },
      variants: {
        [QUOTE_TYPES.NewBusiness]: {
          base: {
            label: $localize`Enquiry open`,
            icon: 'product',
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalInProgress]: {
      base: {
        label: $localize`Proposal in progress`,
        icon: 'application_progress',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalComplete]: {
      base: {
        label: $localize`Proposal complete`,
        icon: 'application_complete',
        level: 'info',
      },
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.LSOS_ADDON]: {
              base: {
                label: $localize`Proposal received`,
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ProposalReleased]: {
      base: {
        label: $localize`Update required`,
        icon: 'application_progress',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.ReviewRequired]: {
      base: {
        label: $localize`Review required`,
        icon: 'under_review',
        level: 'info',
      },
      variants: {
        [QUOTE_TYPES.NewBusiness]: {
          base: {},
          variants: {
            [Partners.NORWAY]: {
              base: {
                label: $localize`Proposal referred`,
                icon: 'exclaim',
                level: 'warning',
              },
            },
            [Partners.NORWAY_PI]: {
              base: {
                label: $localize`Proposal referred`,
                icon: 'exclaim',
                level: 'warning',
              },
            },
          },
        },
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.NORWAY]: {
              base: {
                label: $localize`Proposal referred`,
                icon: 'exclaim',
                level: 'warning',
              },
            },
            [Partners.NORWAY_PI]: {
              base: {
                label: $localize`Proposal referred`,
                icon: 'exclaim',
                level: 'warning',
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteOnHold]: {
      base: {
        label: $localize`On hold`,
        icon: 'under_review',
        level: 'warning',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteHE]: {
      base: {
        label: $localize`Health evaluation required`,
        icon: 'health_evaluation',
        level: 'warning',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteReady]: {
      base: {
        label: $localize`Review required`,
        icon: 'under_review',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteSent]: {
      base: {
        label: $localize`Quote ready`,
        icon: 'quote_universal',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.QuoteAccepted]: {
      base: {
        label: $localize`Quote accepted`,
        icon: 'quote_universal_accepted',
        level: 'success',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.AwaitingPayment]: {
      base: {
        label: $localize`Payment due`,
        icon: 'payment_progress',
        level: 'info',
      },
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.ACCA]: {
              base: {
                label: $localize`Payment outstanding`,
                icon: 'payment_progress',
                level: 'info',
              },
            },
          },
        },
        [QUOTE_TYPES.NewBusiness]: {
          base: {},
          variants: {
            [Partners.ACCA]: {
              base: {
                label: $localize`Payment outstanding`,
                icon: 'payment_progress',
                level: 'info',
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.PaymentProcessing]: {
      base: {
        label: $localize`Processing payment`,
        icon: 'payment_progress',
        level: 'info',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.PaymentReceived]: {
      base: {
        label: $localize`Payment received`,
        icon: 'payment_received',
        level: 'success',
      },
    },
    [QUOTE_WORKFLOW_STATUSES.PolicyDocumentationSent]: {
      base: {
        label: $localize`Policy documentation sent`,
        icon: 'policy_documentation_sent',
        level: 'info',
      },
      variants: {
        [QUOTE_TYPES.Renewal]: {
          base: {},
          variants: {
            [Partners.ACCA]: {
              base: {
                label: $localize`Awaiting Payment`,
                icon: 'payment_progress',
              },
            },
            [Partners.LSOS]: {
              base: {
                label: $localize`Awaiting Payment`,
                icon: 'payment_progress',
              },
            },
            [Partners.LSOS_ADDON]: {
              base: {
                label: $localize`Completed`,
                icon: 'tick',
                supportingText: $localize`Update this status when you have uploaded the policy documents to the portal`,
              },
            },
          },
        },
      },
    },
    [QUOTE_WORKFLOW_STATUSES.Lapsed]: {
      base: {
        label: $localize`Lapsed`,
        icon: 'lapsed',
        level: 'error',
      },
    },
  },
});

export function getPartnerWorkflowMeta(
  status: QuoteWorkflowStatus,
  quoteType: QuoteType,
  partner: Partners | 'DEFAULT' = 'DEFAULT',
  role: RoleType = Role.Broker
) {
  return role === Role.Contact
    ? getContactQuoteWorkflowMeta([status, quoteType, partner])
    : getBrokerQuoteWorkflowMeta([status, quoteType, partner]);
}
